import React, { useEffect, useState } from "react";
import { Alert, Spin, Empty, Card, Row, Col, Tag, Progress, Button, Table, Typography, Select, Space } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getDisplayName } from "../../utils/elementDisplayNames";
import { useGetAdminBrandStatementsQuery, useGetAdminBrandsQuery } from "../../services/nestjs-base-api";
import { ICalculateBrandEquityRequest } from "../../services/nestjs-base-api/types";
import { ThunderboltOutlined, WarningOutlined, AppstoreOutlined, UnorderedListOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Scale, BicepsFlexed } from 'lucide-react';
import { SummaryStatisticCard, StatementCardWithModal } from "../../components/StatementCards";
import { ScoreDistributionModal } from "../../components/ScoreDistributionModal";

const { Title } = Typography;

export const AdminStatementsPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const { brandId } = useParams<{ brandId: string }>();
    const navigate = useNavigate();
    
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [filters, setFilters] = useState<ICalculateBrandEquityRequest>({});
    const [sortBy, setSortBy] = useState<'default' | 'meanScore-asc' | 'meanScore-desc' | 'meanIntensityPercentage-asc' | 'meanIntensityPercentage-desc' | 'meanBrandPowerPercentage-asc' | 'meanBrandPowerPercentage-desc' | 'meanControversyPercentage-asc' | 'meanControversyPercentage-desc'>('default');
    const [scoreDistributionModal, setScoreDistributionModal] = useState<{
        visible: boolean;
        statement?: any;
    }>({ visible: false });
    
    // Get brand info for the header
    const { data: brandsData } = useGetAdminBrandsQuery({
        jwtToken: user?.jwtToken || "",
        page: 1,
        limit: 1000, // Get all brands to find the specific one
    });
    
    const currentBrand = brandsData?.data?.find(b => b.id === parseInt(brandId || ''));
    
    // Local storage key for admin brand-specific filters
    const FILTERS_STORAGE_KEY = `adminBrandEquityFilters_${brandId}_${user?.id || 'default'}`;
    
    // Load filters from localStorage
    useEffect(() => {
        if (!brandId) return;
        
        // Get filters from localStorage (same as admin brand equity page)
        const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
        let loadedFilters: ICalculateBrandEquityRequest = {};
        
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                
                // Migrate old filters: remove obsolete fields
                if (parsed.surveyIds) {
                    delete parsed.surveyIds;
                    parsed.cohortIds = undefined; // Migrate to "All Cohorts"
                }
                delete parsed.filterMode; // Remove obsolete field

                // For preset periods, calculate fresh dates based on current time
                if (parsed.periodType && parsed.periodType !== 'custom') {
                    const now = dayjs();
                    let dateFrom: dayjs.Dayjs;

                    switch (parsed.periodType) {
                        case 'last30':
                            dateFrom = now.subtract(30, 'day');
                            break;
                        case 'last60':
                            dateFrom = now.subtract(60, 'day');
                            break;
                        case 'last90':
                            dateFrom = now.subtract(90, 'day');
                            break;
                        case 'last120':
                            dateFrom = now.subtract(120, 'day');
                            break;
                        case 'last180':
                            dateFrom = now.subtract(180, 'day');
                            break;
                        case 'last365':
                            dateFrom = now.subtract(365, 'day');
                            break;
                        case 'allTime':
                            dateFrom = dayjs('2000-01-01');
                            break;
                        default:
                            dateFrom = now.subtract(30, 'day');
                    }

                    loadedFilters = {
                        dateFrom: dateFrom.toISOString(),
                        dateTo: now.toISOString(),
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined
                    };
                } else if (parsed.dateFrom && parsed.dateTo) {
                    // Custom date range
                    loadedFilters = {
                        dateFrom: parsed.dateFrom,
                        dateTo: parsed.dateTo,
                        cohortIds: parsed.cohortIds?.length > 0 ? parsed.cohortIds : undefined
                    };
                }
            } catch (error) {
                console.error('Failed to parse saved filters:', error);
            }
        }
        
        // If no filters are saved or loaded, use defaults (last 120 days)
        if (!loadedFilters.dateFrom && !loadedFilters.dateTo) {
            const now = dayjs();
            const dateFrom = now.subtract(120, 'day');
            loadedFilters = {
                dateFrom: dateFrom.toISOString(),
                dateTo: now.toISOString(),
                cohortIds: undefined
            };
        }
        
        setFilters(loadedFilters);
    }, [brandId, FILTERS_STORAGE_KEY]);

    // Use RTK Query to get the statements data
    const { data: brandEquityData, isLoading, error, refetch } = useGetAdminBrandStatementsQuery({
        jwtToken: user?.jwtToken || "",
        brandId: parseInt(brandId || '0'),
        filters
    }, {
        skip: !user?.jwtToken || !brandId || Object.keys(filters).length === 0
    });
    
    if (!brandId) {
        return <div>Invalid brand ID</div>;
    }

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading statement sentiment analysis...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error Loading Statement Data"
                    description={'message' in error ? (error as any).message : 'Failed to load data'}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!brandEquityData || !brandEquityData.questionCategoryScores) {
        return (
            <div style={{ padding: '24px' }}>
                <Empty
                    description="No statement data available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    // Function to sort categories
    const sortCategories = (categories: any[]) => {
        if (sortBy === 'default') {
            return [...categories].sort((a, b) => getDisplayName(a.category).localeCompare(getDisplayName(b.category)));
        }

        const [metric, direction] = sortBy.split('-') as [string, 'asc' | 'desc'];
        
        return [...categories].sort((a, b) => {
            const aValue = a[metric] ?? -Infinity;
            const bValue = b[metric] ?? -Infinity;
            
            if (direction === 'desc') {
                return bValue - aValue;
            }
            return aValue - bValue;
        });
    };

    // Get sorted categories for display
    const sortedCategories = brandEquityData ? sortCategories(brandEquityData.questionCategoryScores) : [];

    const sortOptions = [
        { value: 'default', label: 'Default (A → Z)', icon: null },
        { value: 'meanScore-desc', label: 'Score (High → Low)', icon: <Scale size="14px" /> },
        { value: 'meanScore-asc', label: 'Score (Low → High)', icon: <Scale size="14px" /> },
        { value: 'meanIntensityPercentage-desc', label: 'Intensity (High → Low)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanIntensityPercentage-asc', label: 'Intensity (Low → High)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanBrandPowerPercentage-desc', label: 'Brand Power (High → Low)', icon: <BicepsFlexed size="14px" /> },
        { value: 'meanBrandPowerPercentage-asc', label: 'Brand Power (Low → High)', icon: <BicepsFlexed size="14px" /> },
        { value: 'meanControversyPercentage-desc', label: 'Controversy (High → Low)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
        { value: 'meanControversyPercentage-asc', label: 'Controversy (Low → High)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
    ];

    // Extract statement data from questionCategoryScores.input
    const allStatements = brandEquityData.questionCategoryScores
        .flatMap(category => category.input || [])
        .filter(statement => statement.meanScore !== null || statement.intensityValue !== null);

    const getMetricColor = (percentage: number, type: 'polarity' | 'intensity' | 'power' | 'controversy') => {
        if (type === 'controversy') {
            // Controversy: lower is better
            if (percentage <= 20) return '#237804'; // Dark green
            if (percentage <= 40) return '#52c41a'; // Light green
            if (percentage <= 60) return '#faad14'; // Yellow
            if (percentage <= 80) return '#fa8c16'; // Orange
            return '#f5222d'; // Red for 81-100
        }
        
        // Standardized colors for Score, Intensity, and Brand Power
        if (percentage < 0) return '#000000'; // Black for negative values (only Score)
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    const getPolarityColor = (percentage: number) => {
        // Use standardized color scheme for polarity
        if (percentage < 0) return '#000000'; // Black for negative values
        if (percentage === 0) return '#f5222d'; // Red for 0
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };

    const getPolarityExtremeColor = (percentage: number) => {
        // Only return special colors for extreme values, otherwise null
        if (percentage === 100) return '#52c41a'; // Green for +100%
        if (percentage === -100) return '#f5222d'; // Red for -100%
        return null; // Use default colors for all other values
    };

    const tableColumns = [
        {
            title: 'Question ID',
            dataIndex: 'id',
            key: 'id',
            width: '12%',
            render: (id: string) => (
                <Tag color="purple" style={{ fontSize: '11px' }}>
                    {id}
                </Tag>
            ),
            sorter: (a: any, b: any) => a.id.localeCompare(b.id),
        },
        {
            title: 'Statement',
            dataIndex: 'text',
            key: 'text',
            width: '25%',
            render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>
        },
        {
            title: 'Responses',
            dataIndex: 'responseCount',
            key: 'responseCount',
            width: '8%',
            render: (count: number, record: any) => (
                <Tag 
                    color="geekblue"
                    style={{ cursor: record.scoreDistribution ? 'pointer' : 'default' }}
                    onClick={() => {
                        if (record.scoreDistribution) {
                            setScoreDistributionModal({
                                visible: true,
                                statement: record
                            });
                        }
                    }}
                >
                    {count}
                </Tag>
            ),
            sorter: (a: any, b: any) => a.responseCount - b.responseCount,
        },
        {
            title: 'Score',
            dataIndex: 'meanScore',
            key: 'polarity',
            width: '14%',
            render: (value: number | null) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {value !== null && value < 0 && (
                        <span style={{ color: getPolarityExtremeColor(value) || '#f5222d', fontSize: 12 }}>↓</span>
                    )}
                    <Progress
                        percent={Math.abs(value || 0)}
                        size="small"
                        strokeColor={value === null ? '#bfbfbf' : 
                            getPolarityColor(value)}
                        format={() => {
                            if (value === null) return '0%';
                            return `${value >= 0 ? '+' : ''}${value}%`;
                        }}
                        style={{ flex: 1 }}
                    />
                </div>
            ),
            sorter: (a: any, b: any) => (a.meanScore || 0) - (b.meanScore || 0),
        },
        {
            title: 'Intensity',
            dataIndex: 'intensityPercentage',
            key: 'intensity',
            width: '14%',
            render: (value: number | null) => (
                <Progress
                    percent={value || 0}
                    size="small"
                    strokeColor={value === null ? '#bfbfbf' : getMetricColor(value, 'intensity')}
                    format={() => `${value || 0}%`}
                />
            ),
            sorter: (a: any, b: any) => (a.intensityPercentage || 0) - (b.intensityPercentage || 0),
        },
        {
            title: 'Brand Power',
            dataIndex: 'brandPowerPercentage',
            key: 'brandPower',
            width: '14%',
            render: (value: number | null) => (
                <Progress
                    percent={value || 0}
                    size="small"
                    strokeColor={value === null ? '#bfbfbf' : getMetricColor(value, 'power')}
                    format={() => `${value || 0}%`}
                />
            ),
            sorter: (a: any, b: any) => (a.brandPowerPercentage || 0) - (b.brandPowerPercentage || 0),
        },
        {
            title: 'Controversy',
            dataIndex: 'controversyPercentage',
            key: 'controversy',
            width: '13%',
            render: (value: number | null) => (
                <Progress
                    percent={value || 0}
                    size="small"
                    strokeColor={value === null ? '#bfbfbf' : getMetricColor(value, 'controversy')}
                    format={() => `${value || 0}%`}
                />
            ),
            sorter: (a: any, b: any) => (a.controversyPercentage || 0) - (b.controversyPercentage || 0),
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => navigate('/admin/brands')}
                    style={{ marginBottom: 16 }}
                >
                    Back to Brands
                </Button>
                <Title level={2} style={{ margin: 0 }}>
                    Statement Sentiment Analysis
                </Title>
                {currentBrand && (
                    <div style={{ marginTop: 8 }}>
                        <Title level={4} style={{ margin: 0, color: '#666' }}>
                            {currentBrand.name} ({currentBrand.organisationName})
                        </Title>
                    </div>
                )}
            </div>

            {/* Summary Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={12}>
                    <SummaryStatisticCard
                        title="Total Statements"
                        value={allStatements.length}
                        prefix={<UnorderedListOutlined />}
                    />
                </Col>
                <Col xs={12} sm={12}>
                    <SummaryStatisticCard
                        title="Total Responses"
                        value={brandEquityData.totalResponses}
                        prefix={<ThunderboltOutlined />}
                    />
                </Col>
            </Row>

            {/* View Mode Toggle and Sort Controls */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space size="large">
                    {/* Sort Controls */}
                    <Select
                        value={sortBy}
                        onChange={(value) => setSortBy(value)}
                        style={{ width: 270 }}
                        size="middle"
                        placeholder="Sort categories"
                        listHeight={400}
                    >
                        {sortOptions.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                <Space size="small">
                                    {option.icon}
                                    {option.label}
                                </Space>
                            </Select.Option>
                        ))}
                    </Select>
                    {/* View Mode Controls */}
                    <Button.Group>
                        <Button
                            type={viewMode === 'cards' ? 'primary' : 'default'}
                            icon={<AppstoreOutlined />}
                            onClick={() => setViewMode('cards')}
                        >
                            Cards
                        </Button>
                        <Button
                            type={viewMode === 'table' ? 'primary' : 'default'}
                            icon={<UnorderedListOutlined />}
                            onClick={() => setViewMode('table')}
                        >
                            Table
                        </Button>
                    </Button.Group>
                </Space>
                
                <Button onClick={() => refetch()}>
                    Refresh Data
                </Button>
            </div>

            {/* Content */}
            {viewMode === 'cards' ? (
                <div>
                    {sortedCategories.map((category, categoryIndex) => (
                        <div key={category.category} style={{ marginBottom: 48 }}>
                            <h3 style={{ marginBottom: 24, fontSize: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                                {getDisplayName(category.category)}
                                {sortBy !== 'default' && (() => {
                                    const [metric] = sortBy.split('-');
                                    const value = category[metric];
                                    if (value == null) return null;
                                    
                                    let metricName = '';
                                    let metricType: 'polarity' | 'intensity' | 'power' | 'controversy' = 'polarity';
                                    
                                    switch (metric) {
                                        case 'meanScore':
                                            metricName = 'Score';
                                            metricType = 'polarity';
                                            break;
                                        case 'meanIntensityPercentage':
                                            metricName = 'Intensity';
                                            metricType = 'intensity';
                                            break;
                                        case 'meanBrandPowerPercentage':
                                            metricName = 'Brand Power';
                                            metricType = 'power';
                                            break;
                                        case 'meanControversyPercentage':
                                            metricName = 'Controversy';
                                            metricType = 'controversy';
                                            break;
                                    }
                                    
                                    const displayValue = Math.round(value);
                                    const isNegative = value < 0;
                                    const color = (metricType === 'polarity' || metricType === 'power') ? 
                                        getPolarityColor(value) : 
                                        getMetricColor(value, metricType);
                                    
                                    return (
                                        <Tag
                                            style={{ 
                                                fontSize: 14, 
                                                padding: '4px 12px',
                                                fontWeight: 600,
                                                marginLeft: 0,
                                                backgroundColor: color + '15',
                                                borderColor: color,
                                                color: color
                                            }}
                                        >
                                            {isNegative ? '' : '+'}{displayValue}% {metricName}
                                        </Tag>
                                    );
                                })()}
                                {sortBy === 'default' && category.meanScore !== null && (
                                    <Tag
                                        style={{ 
                                            fontSize: 14, 
                                            padding: '4px 12px',
                                            fontWeight: 600,
                                            marginLeft: 0,
                                            backgroundColor: getMetricColor(category.meanScore, 'intensity') + '15',
                                            borderColor: getMetricColor(category.meanScore, 'intensity'),
                                            color: getMetricColor(category.meanScore, 'intensity')
                                        }}
                                    >
                                        {Math.round(category.meanScore)}%
                                    </Tag>
                                )}
                            </h3>
                            
                            {category.input && category.input.length > 0 ? (
                                <Row gutter={[24, 24]}>
                                    {category.input.map((statement: any, statementIndex: number) => {
                                        const anchorId = `${category.category.toLowerCase().replace(/\s+/g, '-')}-${statement.id}`;
                                        
                                        return (
                            <Col xs={24} lg={12} xl={8} key={statement.id}>
                                <div id={anchorId}>
                                    <StatementCardWithModal
                                        statement={statement}
                                        isAdmin={true}
                                    />
                                </div>
                            </Col>
                        );
                    })}
                                </Row>
                            ) : (
                                <Empty description="No statements available for this category" />
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <Card>
                    <Table
                        dataSource={allStatements}
                        columns={tableColumns}
                        rowKey={(record, index) => index?.toString() || '0'}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} statements`
                        }}
                        scroll={{ x: 800 }}
                    />
                </Card>
            )}

            {/* Score Distribution Modal for Table View */}
            {viewMode === 'table' && scoreDistributionModal.statement && (
                <ScoreDistributionModal
                    visible={scoreDistributionModal.visible}
                    onClose={() => setScoreDistributionModal({ visible: false })}
                    statementText={scoreDistributionModal.statement.text}
                    responseCount={scoreDistributionModal.statement.responseCount}
                    scoreDistribution={scoreDistributionModal.statement.scoreDistribution}
                    questionType={
                        scoreDistributionModal.statement.questionTypeId === 1 ? 'likert7' :
                        scoreDistributionModal.statement.questionTypeId === 7 ? 'nps' : 
                        'other'
                    }
                />
            )}
        </div>
    );
};